import React, { useState, useRef, useMemo, useEffect } from "react";
import { hasRPDataActive, hideUpgradePanel, isStarterPlan } from "../../utils/commonUtil";
import { useNavigate } from "react-router-dom";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import { REIButton, REIProgressSpinner } from "../../components/ui";
import { UpgradeToProfessional } from "../../components/shared";
import { httpPost } from "../../services/httpService";
import { ApiEndPoints } from "../../utils/endpoints";
import { USER_DETAIL } from "../../utils/constants";
import { openRPDataLogin } from "../../utils/commonUtil";
import { CotalityLogo as RPDATALogo } from "../../public/assets/images";

const RPData: React.FC = () => {
  const toast = useRef<Toast>(null);
  const [showEnquiryModal, setShowEnquiryModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    email: "",
    message: "",
  });
  const memberDetail = useMemo(() => JSON.parse(localStorage.getItem(USER_DETAIL) || "{}"), []);
  const memberName = `${memberDetail.FirstName} ${memberDetail.LastName}`;
  const hasAccess = hasRPDataActive();
  const navigate = useNavigate();
  const opened = useRef(false);

  useEffect(() => {
    if (!hasAccess || opened.current) return;
    opened.current = true;
    openRPDataLogin();
    if (window.history.state?.idx > 0) {
      navigate(-1);
    } else {
      navigate("/", { replace: true });
    }
  }, [hasAccess, navigate]);

  const features = [
    {
      title: "Comprehensive Property Insights",
      icon: "pi-chart-line",
      items: [
        "Instantly access desktop valuation estimates for any Australian residential property.",
        "View recent sales history, listings, rentals and comparable market data to support confident buying decisions.",
        "Dive deeper into individual property attributes including land size, development zoning and ownership details (where available).",
      ],
    },
    {
      title: "Property & Suburb Analysis Tools",
      icon: "pi-map",
      items: [
        "Track days on market, rental history and listing trends for properties across Australia.",
        "Access detailed suburb data such as median price, median rent and average market activity.",
        "Use mapping and overlay features to visualise recent transactions and key market indicators.",
      ],
    },
  ];

  const detailedFeatures = [
    "Monthly Casual or Annual Upfront Billing",
    "National access",
    "Detailed property data",
    "Property search",
    "Market activity",
    "Property valuation estimate",
    "Rental valuation estimate",
    "Basic mapping",
    "Building consent history",
    "Ownership search",
    "Sales CMA",
    "Rental CMA",
    "Saved searches",
    "Saved lists",
    "Up to 10,000 Property Exports per Month",
    "Digital property reports",
  ];

  async function sendUpgradeInquiryEmail() {
    {
      setLoading(true);
      await httpPost(`${ApiEndPoints.rpData}/UpgradeRequestEmail`, {
        UserName: memberName,
        Email: memberDetail.EmailAddress,
        Message: form.message,
      })
        .then((result) => {
          if (result.status == 200) {
            if (result.data !== null) {
              toast.current?.show({
                severity: "success",
                summary: "Enquiry Sent",
                detail: "Our support team will contact you shortly.",
                life: 5000,
              });
            } else {
              return <REIProgressSpinner></REIProgressSpinner>;
            }
          }
        })
        .catch(() => {});
      setLoading(false);
    }
  }

  const handleSubmit = async () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setShowEnquiryModal(false);
      setForm({ email: "", message: "" });
      sendUpgradeInquiryEmail();
    }, 1500);
  };

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <div className="min-h-[calc(100vh-100px)] sm:px-4 md:py-8 md:px-6 lg:px-10 text-slate-900">
        <div className="w-full">
          <section className="overflow-hidden bg-white relative">
            <div className="h-1.5 bg-gradient-to-r from-blue-400 via-[var(--primary-color)] to-blue-400" />

            <div className="relative px-4 py-6 md:px-10 md:py-10 bg-gradient-to-b from-white to-slate-50 z-10 text-center">
              <div className="max-w-4xl mx-auto flex flex-col items-center">
                <div className="max-w-[1200px] mx-auto text-center text-xs">
                  <img src={RPDATALogo} alt="RP Data" className="text-sm max-h-32 mx-auto" />
                </div>
                <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold text-black mb-2 mt-4 md:mb-3 md:mt-3 tracking-wide">
                  RP Data by Cotality
                </h1>
                <p className="text-slate-600 text-base sm:text-lg md:text-xl leading-relaxed max-w-2xl mx-auto">
                  Unlock national property data at exclusive rates. Make confident investment decisions with
                  professional-grade insights
                </p>
                <div className="mt-2 flex flex-col justify-center gap-4 sm:flex-row w-full sm:w-auto">
                  {isStarterPlan() && !hideUpgradePanel && (
                  <UpgradeToProfessional
                      onUpgraded={() => window.location.reload()}
                    idPrefix="rpd-upg"
                    buttonLabel="Upgrade to Professional"
                  />
                  )}
                  <REIButton
                    id="rpd-btn-enquiryhero"
                    label="Upgrade Enquiry"
                    onClick={() => setShowEnquiryModal(true)}
                    classNames="rounded-lg bg-white border border-solid border-brand-teal px-8 py-3.5 text-sm md:text-base font-semibold text-brand-teal shadow-md hover:opacity-90 w-full sm:w-auto"
                    icon="pi pi-arrow-right"
                    iconPos="right"
                  />
                </div>
              </div>
            </div>
          </section>

          <section className="rounded-lg overflow-hidden bg-gray-100">
            <div className="px-4 md:px-10 py-8 md:py-12 bg-gray-100 relative">
              <div className="max-w-4xl mx-auto text-center mb-10">
                <h2 className="text-3xl md:text-4xl font-bold text-black mt-2">RP Data product features</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {detailedFeatures.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 p-4 rounded-lg bg-white hover:bg-gray-100 transition-all duration-300 group"
                  >
                    <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-green-500 flex-shrink-0">
                      <i className="pi pi-check text-sm font-bold"></i>
                    </div>
                    <span className="text-gray-700 font-medium text-sm md:text-base leading-snug">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
          <section id="features-section" className="overflow-hidden bg-white">
            <div className="px-4 md:px-10 py-8 md:py-12 relative bg-white">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-12 bg-gray-200 to-transparent" />

              <h2 className="text-2xl md:text-[1.7rem] font-bold text-black tracking-wide mb-3 text-center">
                Features designed for investors
              </h2>
              <p className="text-center text-slate-500 text-base md:text-lg mb-8 max-w-2xl mx-auto">
                Everything you need to analyze properties and markets with confidence.
              </p>

              <div className="flex flex-col gap-6 md:gap-8 max-w-5xl mx-auto">
                {features.map((feature, idx) => (
                  <div
                    key={idx}
                    className="relative flex flex-col md:flex-row gap-6 lg:gap-10 bg-white p-6 md:p-8 rounded-lg transition-all duration-300 group overflow-hidden items-stretch"
                  >
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-gray-200 group-hover:bg-cyan-500 transition-colors duration-300" />

                    <div className="absolute -right-6 -top-6 text-[8rem] text-slate-200/40 group-hover:text-blue-100/40 transition-colors rotate-12 pointer-events-none select-none z-0">
                      <i className={`pi ${feature.icon}`}></i>
                    </div>

                    <div className="relative z-10 w-full md:w-1/3 shrink-0 flex flex-col items-start gap-4">
                      <div className="w-16 h-16 rounded-lg bg-gray-100 text-cyan-600 flex items-center justify-center text-3xl group-hover:bg-gray-200 group-hover:scale-110 transition-all duration-300 relative">
                        <i className={`pi ${feature.icon} z-10`}></i>
                        <div className="absolute inset-0 bg-blue-100 rounded-2xl blur-xl opacity-0 group-hover:opacity-60 transition-opacity"></div>
                      </div>

                      <div className="mt-2">
                        <h3 className="text-xl md:text-2xl font-bold text-black group-hover:text-black transition-colors leading-tight mt-1">
                          {feature.title}
                        </h3>
                        <div className="h-1 w-16 bg-gray-200 mt-4 rounded-full group-hover:w-full group-hover:bg-cyan-500 transition-all duration-500"></div>
                      </div>
                    </div>

                    <div className="hidden md:block w-px bg-gray-200 my-2 group-hover:bg-gray-300 transition-colors relative z-10"></div>

                    <div className="relative z-10 w-full md:w-2/3 flex flex-col justify-center">
                      <ul className="space-y-3">
                        {feature.items.map((item, i) => (
                          <li
                            key={i}
                            className="flex items-start gap-4 text-base md:text-[1.05rem] leading-relaxed text-gray-700 bg-gray-50 hover:bg-white p-4 rounded-lg transition-colors"
                          >
                            <i className="pi pi-check-circle mt-0.5 text-lg text-green-500 shrink-0"></i>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="mx-auto overflow-hidden bg-white relative">
            <div className="relative px-6 md:px-10 py-8 md:py-10 bg-gray-100">
              <div className="text-center mb-6">
                <h2 className="text-xl md:text-2xl font-semibold text-black tracking-tight leading-tight">
                  Talk to our team about RP Data access
                </h2>
              </div>

              <div className="flex justify-center">
                  <REIButton
                    id="rpd-btn-enquirycontact"
                    label="Upgrade Enquiry"
                    onClick={() => setShowEnquiryModal(true)}
                  classNames="bg-brand-teal hover:bg-brand-tealDark border-none font-semibold px-8 py-3 transition-all text-white text-base"
                  />
              </div>
            </div>
          </section>
        </div>

        <Dialog
          header="Upgrade Enquiry"
          visible={showEnquiryModal}
          style={{ width: "90vw", maxWidth: "500px" }}
          onHide={() => setShowEnquiryModal(false)}
          draggable={false}
          className="rei-dialog"
        >
          <div className="flex flex-col gap-4 pt-2">
            <p className="text-sm text-slate-600 mb-2">
              Interested in upgrading? Send us a message and our support team will get back to you with options.
            </p>

            <div className="flex flex-col gap-2">
              <label htmlFor="email" className="text-sm font-bold text-slate-700">
                Email Address
              </label>
              <InputText
                id="email"
                data-testid="rpd-txt-email"
                value={memberDetail.EmailAddress}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Enter your email"
                className="w-full"
                disabled
              />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="rpd-txt-subject" className="text-sm font-bold text-slate-700">
                Subject
              </label>
              <InputText
                id="rpd-txt-subject"
                data-testid="rpd-txt-subject"
                value="Upgrade Enquiry"
                disabled
                className="w-full bg-slate-50 text-slate-500"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="message" className="text-sm font-bold text-slate-700">
                Message
              </label>
              <InputTextarea
                id="message"
                data-testid="rpd-txt-message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={4}
                placeholder="Hi, I'd like to know more about the Cotality RP Data upgrade options..."
                className="w-full resize-none"
              />
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <REIButton
                id="rpd-btn-cancel"
                label="Cancel"
                onClick={() => setShowEnquiryModal(false)}
                classNames="bg-white text-slate-600 border border-slate-300"
              />
              <REIButton
                id="rpd-btn-submit"
                label={loading ? "Sending..." : "Submit Enquiry"}
                onClick={handleSubmit}
                classNames="bg-[var(--primary-color)] border-none"
                disabled={loading || !form.message}
              />
            </div>
          </div>
        </Dialog>
      </div>
    </>
  );
};

export default RPData;
